import { IMAGES, kiosk_IMAGES } from "@/assets/IMAGES";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardFooter } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import { useGetAllkioskDepartmentQuery, useLazyGetAllDepartmentServiceByIdQuery } from "@/services/SelfServiceKiosk";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
const { VITE_VIEW_S3_URL } = import.meta.env;
import { default as defaultImg } from "@/assets/default.jpg";
import useTableData from "@/components/ui/table/DataTableLogic";
import usePagination from "@/hooks/UsePagination";
import { Department } from "@/app/types/department";
import { useToast } from "@/components/ui/UseToast";
const getCurrentDateTime = () => {
  const now = new Date();
  const time = now.toLocaleTimeString("en-GB", { hour12: true }); // 24-hour format
  const date = now.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return { time, date };
};

const SkeletonCard: React.FC = () => (
  <div className="animate-pulse group relative flex flex-col items-center justify-center w-full py-4 bg-[#EDEDED]">
    <div className="w-full h-[150px] bg-gray-300 rounded-md"></div>
    <div className="mt-4 w-3/4 h-4 bg-gray-300 rounded-md"></div>
  </div>
);

const initialValue = {
  limit: 8,
  offset: 0
}

const { VITE_IS_PRODUCTION, VITE_IS_LOCAL, VITE_LOCAL_URL } = import.meta.env;

const KioskHomePage: React.FC = () => {
  const { time, date } = getCurrentDateTime();
  const { toast } = useToast();
  const itemsPerPage = 8; // Number of cards per page
  const [currentPage, setCurrentPage] = useState(0); // Tracks the current page
  const navigate = useNavigate();
  const { searchCriteria, query, setSearchCriteria } = useTableData(initialValue);

  const { data: kioskData, isLoading } = useGetAllkioskDepartmentQuery({
    query: `${query}&domain=${VITE_IS_LOCAL === "true"
      ? VITE_LOCAL_URL
      : VITE_IS_PRODUCTION === "true"
        ? window.location.hostname
        : window.location.host}`,
  });

  const totalRecords = kioskData?.total_count ?? 0;


  const {
    handleNextPage,
    handlePrevPage,
    isLastPage
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  // Pagination logic
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = kioskData?.data?.slice(startIndex, endIndex) || [];

 const [trigger, { data, isError }] =
    useLazyGetAllDepartmentServiceByIdQuery();

    const handleCardClick = async (cardData: Department) => {
      try {
        const response = await trigger({
          department_id: cardData?.id ?? 0,
          query: "ignore_schedule=false",
        })
          .unwrap()
          .then(() => {
            navigate(`/kiosk-service-offer/${cardData.id}`, {
              state: { department: cardData },
            });
          })
          .catch(() => {
            toast({
              variant: "destructive",
              description: "No Services Available for this Department.",
            });
            return;
          });
      } catch (error) {
        toast({
          variant: "destructive",
          description: "An error occurred. Please try again.",
        });
        console.error("Error fetching department:", error);
      }
    };
  
  const renderNoDepartmentMessage = () => (
    <div className="text-center py-6 flex justify-center items-center">
      <h2 className="text-gray-600 text-[1.2rem] font-semibold">
        No Department available
      </h2>
    </div>
  );

  return (
    <main className="w-full flex flex-col items-center justify-center p-0 md:p-1 bg-gray-50">
      <div className="m-0 md:m-3 w-[80%]">
        {/* Header */}
        <div className="flex py-3 md:py-0 flex-col items-center justify-center md:flex-row md:justify-between">
          <div className="flex items-center">
            <img
              src={IMAGES.logo_icon}
              alt="logo"
              className="w-[5rem] h-[5rem] object-contain"
            />
            <p className="text-primary text-[1.4rem] font-bold">QMS</p>
          </div>
          <h4 className="my-2 md:my-0 text-[1.5rem] md:text-[2rem]">
            Self Service Kiosk
          </h4>
        </div>

        {/* Services Section */}
        <section
          className={`${currentServices.length > 0 ? "mx-4 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 my-0 md:mt-8" : ""}`}
        >
          {(() => {
            if (isLoading) {
              return Array.from({ length: itemsPerPage }).map((_, index) => (
                <SkeletonCard key={Number(index)} />
              ));
            } else if (currentServices.length > 0) {
              return currentServices.map((item, index) => (
                <Card
                  key={Number(index)}
                  onClick={() => handleCardClick(item)}
                  className="group relative flex flex-col items-center justify-center w-full py-4 bg-[#EDEDED] hover:cursor-pointer"
                >
                  <CardContent className="flex flex-col items-center justify-center text-center leading-0">
                    <div className="w-[100%] h-[100%]">
                      <img
                        src={
                          item.logo
                            ? `${VITE_VIEW_S3_URL + item.logo}`
                            : defaultImg
                        }
                        alt="Company logo"
                        className="w-[150px] h-[150px] object-contain"
                      />
                    </div>
                  </CardContent>

                  <CardFooter className="flex items-center justify-center text-center">
                    <p className="capitalize text-[#767676] m-0 group-hover:text-white p-0 font-semibold text-[1.2rem]">
                      {item.name}
                    </p>
                  </CardFooter>
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80 text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="text-center px-2">
                      <p className="capitalize text-[1.2rem] font-bold m-0">
                        {item.name}
                      </p>
                    </div>
                  </div>
                </Card>
              ));
            } else {
              return renderNoDepartmentMessage();
            }
          })()}
        </section>

        {/* Pagination Controls */}
        <div className="flex items-center justify-end gap-4 my-4 w-full">
          {searchCriteria.offset !== 0 && <Button
            variant="solid"
            size="icon"
            onClick={handlePrevPage}
            disabled={searchCriteria.offset === 0}
            className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-7 w-7" />
          </Button>}

          <Button
            variant="solid"
            size="icon"
            onClick={handleNextPage}
            disabled={isLastPage}
            className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-7 w-7" />
          </Button>
        </div>

        <Separator
          orientation="horizontal"
          className="h-[.1rem] w-[100%] bg-gray-300"
        />

        {/* Footer */}
        <div className="flex items-center justify-between my-4">
          <div>
          </div>
          <div className="bg-gray-300 text-gray-500 p-2 rounded-full">
            <p className="text-[.9rem]">Information & Contact</p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default KioskHomePage;
